<template>
  <!--
    TASK-SF-08 -- DESIGN-10, the student نمایه READ view. An additive branch
    inside ProfileView.vue (one v-if); the volunteer/legacy tree is
    byte-identical and untouched.

    Renders ONLY real persisted data from GET /api/student/profile. Nothing
    is fabricated: a value with no data source is shown with its canonical
    empty state («هنوز ثبت نشده» / «ثبت نشده») or omitted entirely -- never
    zero-filled.

    SX-01 (doc 22 §7, DESIGN-19): rendered-V2 parity. The composition is the
    reference's own -- profile head, the three stat tiles (real Reports
    summary data under the DESIGN-18 rules, H-A1), the «اطلاعات نمایه» card
    with its «ویرایش اطلاعات» button, the role-opportunity card. The legacy
    settings list is gone (V2 removes it). No avatar upload / presets (H-A2:
    no backend field). Geometry and type come from the Borna tokens; the page
    itself is registered on the Borna surface list.
  -->
  <div class="student-profile" data-testid="student-profile-card">
    <!-- loading -->
    <div
      v-if="loading"
      class="student-profile__skeleton"
      data-testid="student-profile-loading"
      role="status"
      aria-live="polite"
    >
      <span class="student-profile__skeleton-line"></span>
      <span class="student-profile__skeleton-line"></span>
      <span class="student-profile__skeleton-line"></span>
    </div>

    <!-- error -->
    <div
      v-else-if="loadError"
      class="student-profile__error alert alert-danger"
      role="alert"
      data-testid="student-profile-error"
    >
      {{ $t('profile.student.loadError') }}
      <button
        type="button"
        class="uc-link-button"
        data-testid="student-profile-retry"
        @click="fetchProfile"
      >
        {{ $t('profile.student.retry') }}
      </button>
    </div>

    <template v-else>
      <!-- transient save-success notice, handed down from ProfileView after
           a successful PATCH; shown once per remount, never persisted. -->
      <p
        v-if="localSavedNotice"
        class="student-profile__saved"
        role="status"
        data-testid="student-profile-saved"
      >
        {{ $t('profile.student.savedNotice') }}
      </p>

      <!-- profile head (V2 `.profile-head`): letter avatar, name, «پایه · رشته» -->
      <section class="student-profile__identity">
        <span class="student-profile__avatar" aria-hidden="true">{{
          avatarInitial
        }}</span>
        <div class="student-profile__identity-text">
          <h1 class="student-profile__name" data-testid="student-profile-name">
            {{ fullName }}
          </h1>
          <p
            v-if="summaryLine"
            class="student-profile__summary"
            data-testid="student-profile-summary"
          >
            {{ summaryLine }}
          </p>
        </div>
      </section>

      <!-- V2 `.profile-stats`: real Reports summary, never fabricated -->
      <student-profile-stats />

      <!-- اطلاعات نمایه (V2 `.student-profile-details`) -->
      <section class="student-profile__block">
        <div class="student-profile__block-head">
          <h2 class="student-profile__block-heading">
            {{ $t('profile.student.infoHeading') }}
          </h2>
          <button
            type="button"
            class="student-profile__edit"
            data-testid="student-profile-edit-btn"
            @click="$emit('edit')"
          >
            <student-icon name="edit" :size="15" />
            {{ $t('profile.student.editInfo') }}
          </button>
        </div>
        <dl class="student-profile__grid">
          <div class="student-profile__row">
            <dt>{{ $t('profile.student.province') }}</dt>
            <dd data-testid="student-profile-province">
              <template v-if="profile && profile.province">{{
                provinceDisplay
              }}</template>
              <span v-else class="student-profile__empty">{{
                provinceDisplay
              }}</span>
            </dd>
          </div>
          <div class="student-profile__row">
            <dt>{{ $t('profile.student.city') }}</dt>
            <dd data-testid="student-profile-city">
              <template v-if="hasCity">{{ cityDisplay }}</template>
              <span v-else class="student-profile__empty">{{
                cityDisplay
              }}</span>
            </dd>
          </div>
          <div class="student-profile__row">
            <dt>
              {{ $t('profile.student.schoolName') }}
              <small class="student-profile__optional">{{
                $t('profile.student.optional')
              }}</small>
            </dt>
            <dd data-testid="student-profile-school">
              <template v-if="profile && profile.schoolName">{{
                schoolDisplay
              }}</template>
              <span v-else class="student-profile__empty">{{
                schoolDisplay
              }}</span>
            </dd>
          </div>
          <div class="student-profile__row">
            <dt>
              {{ $t('profile.student.emailLabel') }}
              <small class="student-profile__optional">{{
                $t('profile.student.optional')
              }}</small>
            </dt>
            <dd class="ltr" data-testid="student-profile-email">
              <bdi v-if="profile && profile.email">{{ profile.email }}</bdi>
              <span v-else class="student-profile__empty">{{
                emailDisplay
              }}</span>
            </dd>
          </div>
        </dl>
      </section>

      <!-- TASK-SF-10: role-upgrade entry point into the existing volunteer
           flow (visual parity SX-01; its CTA is gated by SX-02 while the
           Student teacher capability is OFF). -->
      <volunteer-upgrade-card />
      <slot name="upgrade" />
    </template>
  </div>
</template>

<script>
import StudentService from '@/services/StudentService'
import VolunteerUpgradeCard from './VolunteerUpgradeCard.vue'
import StudentProfileStats from './StudentProfileStats.vue'
import StudentIcon from './StudentIcon.vue'

export default {
  name: 'StudentProfileCard',
  components: { VolunteerUpgradeCard, StudentProfileStats, StudentIcon },
  props: {
    // Set by ProfileView right after a successful edit-save, so the read
    // card (which the edit form hands control back to) can show a brief
    // accessible confirmation. Read once at creation time -- ProfileView
    // remounts this component (bumped :key) on every save, so a fresh
    // instance always picks up the current value.
    savedNotice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      profile: null,
      localSavedNotice: false,
    }
  },
  computed: {
    fullName() {
      if (!this.profile) return ''
      return [this.profile.firstName, this.profile.lastName]
        .filter(Boolean)
        .join(' ')
    },
    avatarInitial() {
      const n = (this.profile && this.profile.firstName) || ''
      return n ? n.trim().charAt(0) : ''
    },
    // «دهم · رشته ریاضی و فیزیک» -- built only from persisted values; if
    // neither is set the line is omitted entirely (no fabrication).
    summaryLine() {
      if (!this.profile) return ''
      const parts = []
      if (this.profile.educationGrade) {
        parts.push(this.profile.educationGrade.displayName)
      }
      if (this.profile.studyField) {
        parts.push(
          this.$t('profile.student.fieldPrefix') +
            this.profile.studyField.displayName
        )
      }
      return parts.join(this.$t('profile.student.summarySeparator'))
    },
    provinceDisplay() {
      return this.profile && this.profile.province
        ? this.profile.province.displayName
        : this.$t('profile.student.notSetLong')
    },
    hasCity() {
      const city = this.profile && this.profile.city
      return Boolean(city && (city.type === 'lookup' || city.type === 'manual'))
    },
    // Tagged union: lookup -> displayName, manual -> raw value, null -> empty
    // state. Branches on city.type, never on presence of a plain string.
    cityDisplay() {
      const city = this.profile && this.profile.city
      if (!city) return this.$t('profile.student.notSetLong')
      if (city.type === 'lookup') return city.displayName
      if (city.type === 'manual') return city.value
      return this.$t('profile.student.notSetLong')
    },
    schoolDisplay() {
      return (
        (this.profile && this.profile.schoolName) ||
        this.$t('profile.student.notSet')
      )
    },
    emailDisplay() {
      return (
        (this.profile && this.profile.email) ||
        this.$t('profile.student.notSet')
      )
    },
  },
  created() {
    if (this.savedNotice) {
      this.localSavedNotice = true
      this._savedNoticeTimer = setTimeout(() => {
        this.localSavedNotice = false
      }, 4000)
    }
    this.fetchProfile()
  },
  beforeDestroy() {
    if (this._savedNoticeTimer) clearTimeout(this._savedNoticeTimer)
  },
  methods: {
    async fetchProfile() {
      this.loading = true
      this.loadError = false
      try {
        this.profile = await StudentService.getProfile()
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// SX-01: every value below is measured from the rendered V2 Profile
// (`.profile-head` as mutated by the reference's own enhancement layer,
// `.student-profile-details`, `.student-profile-details-edit`) at 1440 and
// 390; colours/radii are the shared Borna tokens.
.student-profile {
  // Student Journey V2: the shared Student page box (same gutters as Home,
  // the tutor setup and Reports), around the unchanged 720px column.
  @include borna-page-container(720px);
}

.student-profile__saved {
  margin: 0 0 16px;
  padding: 10px 14px;
  border: 1px solid $borna-indigo-100;
  border-radius: $borna-radius-md;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-size: 12px;
  line-height: 1.8;
}

// ---- profile head ----------------------------------------------------------
.student-profile__identity {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 22px;
  border: 1px solid $borna-border;
  border-radius: 22px;
  background: $borna-surface;
  box-shadow: 0 10px 28px rgba(7, 20, 71, 0.055);
}

.student-profile__avatar {
  display: grid;
  place-items: center;
  flex: 0 0 72px;
  width: 72px;
  height: 72px;
  border-radius: 22px;
  background: $borna-indigo-600;
  box-shadow: 0 12px 28px rgba(49, 75, 214, 0.16);
  color: #fff;
  font-size: 24px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
}

.student-profile__identity-text {
  min-width: 0;
  flex: 1;
}

.student-profile__name {
  margin: 0 0 5px;
  font-size: 17px;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-text;
  overflow-wrap: anywhere;
}

.student-profile__summary {
  margin: 0;
  font-size: 11px;
  line-height: 1.75;
  color: $borna-muted;
}

// ---- اطلاعات نمایه ----------------------------------------------------------
.student-profile__block {
  margin: 0 0 18px;
  padding: 18px;
  border: 1px solid $borna-border;
  border-radius: 20px;
  background: $borna-surface;
}

.student-profile__block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 14px;
}

.student-profile__block-heading {
  margin: 0;
  font-size: 15px;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-text;
}

// V2 `.student-profile-details-edit`
.student-profile__edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 10px;
  border: 1px solid $borna-indigo-100;
  border-radius: 10px;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font: inherit;
  font-size: 11px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.75;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    border-color: $borna-indigo-300;
  }
}

.student-profile__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin: 0;
}

.student-profile__row {
  min-width: 0;
}

.student-profile__row dt {
  margin: 0;
  font-size: 12px;
  // Bootstrap's Reboot bolds every <dt>; V2's terms are regular weight.
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-muted;
}

.student-profile__optional {
  margin-inline-start: 5px;
  font-size: 11px;
  font-weight: $borna-fw-regular;
  color: $borna-muted;
}

.student-profile__row dd {
  margin: 3px 0 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.75;
  color: $borna-text;
  overflow-wrap: anywhere;
}

// V2 empty state (`dd > span`): a lighter, regular-weight line.
.student-profile__empty {
  font-size: 12px;
  font-weight: $borna-fw-regular;
  color: $borna-muted;
}

// ---- states the reference does not draw --------------------------------------
.student-profile__skeleton-line {
  display: block;
  height: 14px;
  margin-bottom: 10px;
  border-radius: 4px;
  background: $borna-border;
}

.student-profile__error {
  border-radius: $borna-radius-md;
}

.ltr {
  direction: ltr;
  unicode-bidi: isolate;
}

@media (min-width: 621px) {
  .student-profile__grid {
    grid-template-columns: 1fr 1fr;
  }
}

// V2 `@media (max-width: 720px)`: tighter head, 62px avatar.
@include borna-compact {
  .student-profile__identity {
    gap: 12px;
    padding: 16px;
  }

  .student-profile__avatar {
    flex-basis: 62px;
    width: 62px;
    height: 62px;
    border-radius: 19px;
    font-size: 21px;
  }
}
</style>
